import { createSelector } from 'reselect'

import {
    isQuoteTypeIm,
    REORDERING,
    REQUESTED,
    DRAFT,
    PURCHASABLE,
} from '~/lib/quoteStatuses'
import { PROVISIONED_UI_FLAGS } from '~/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/logic'
import { getUserIdentity } from '~/redux-modules/identity'
import isFlagEnabled from '~/lib/isFlagEnabled'
import { DefaultRootState } from '~/types'
import { FictivStateType } from '~/redux-modules/fictivStateType'
import { IdentityType } from '../identity/type'

import {
    INITIAL_STATE_TYPE,
    CORE_SUCCESS,
    isNoPartState,
    isQuoteRequestedState,
    isConfiguredState,
    isRfqType,
    isUnconfiguredPartsState,
} from './types'

const TEST_REORDER_ORDER_NAME = 'Test1234' // API's not returning correctly yet, returns null

/**
 * @deprecated Assume flag is always true
 */
export const isQueryDetailsQueryRefactorOn = (state: FictivStateType) =>
    isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.QUOTE_DETAILS_QUERY_REFACTOR,
        'true',
    )
export const shouldHideInternationalOrderFlag = (state: DefaultRootState) =>
    isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.HIDE_INTERNATIONAL_ORDERING,
        'true',
    )
export const isPurchasableStateEnabled = (identity: IdentityType) =>
    isFlagEnabled(identity, PROVISIONED_UI_FLAGS.ADD_PURCHASABLE_STATE, 'true')

export const isCoreSuccessState = (
    state: INITIAL_STATE_TYPE | CORE_SUCCESS,
): state is CORE_SUCCESS => {
    return (
        !!state.coreQuoteDetails?.coreQuoteDetailsData && state.error === false
    )
}

export const isCoreLoadingState = (
    state: INITIAL_STATE_TYPE | CORE_SUCCESS,
): state is CORE_SUCCESS => {
    return (
        !state.coreQuoteDetails?.coreQuoteDetailsData && state.loading === true
    )
}

export const filesTabHeaderSelector = createSelector(
    (state: FictivStateType) => state.queryRefactor.coreQuoteDetails,
    (state: FictivStateType) => isCoreSuccessState(state.queryRefactor),
    (coreQuoteDetails, isCoreQuerySuccess) => {
        const isQuotePurchasable =
            coreQuoteDetails.coreQuoteDetailsData?.isQuotePurchasable
        const slowestShipDateEstimate =
            coreQuoteDetails.coreQuoteDetailsData?.slowestShipDateEstimate
        const isImQuoteType = isQuoteTypeIm(
            coreQuoteDetails.coreQuoteDetailsData?.type,
        )
        const isReorder =
            coreQuoteDetails.coreQuoteDetailsData?.status === REORDERING
        const earliestValidUntilDate =
            coreQuoteDetails.coreQuoteDetailsData?.earliestValidUntilDate
        const orderName =
            coreQuoteDetails.coreQuoteDetailsData?.reorderSourceOrderName ||
            TEST_REORDER_ORDER_NAME
        const quoteId = coreQuoteDetails.coreQuoteDetailsData?.id
        const lastModified = coreQuoteDetails.coreQuoteDetailsData?.lastModified
        const quoteName = coreQuoteDetails.coreQuoteDetailsData?.name
        const project = coreQuoteDetails.coreQuoteDetailsData?.project
        const quoteStatus = coreQuoteDetails.coreQuoteDetailsData?.status
        const quoteOwner = coreQuoteDetails.coreQuoteDetailsData?.user
        const team = coreQuoteDetails.coreQuoteDetailsData?.team || null
        const attachmentLink =
            coreQuoteDetails.coreQuoteDetailsData?.quoteModels?.items[0]
                ?.selectedQuoteModelRevision.attachmentLink

        return {
            isQuotePurchasable,
            slowestShipDateEstimate,
            isImQuoteType,
            isReorder,
            earliestValidUntilDate,
            orderName,
            quoteId,
            lastModified,
            quoteName,
            project,
            isCoreQuerySuccess,
            quoteStatus,
            quoteOwner,
            attachmentLink,
            team,
        }
    },
)

export const quoteLevelLeadTimeSelecter = createSelector(
    (state: FictivStateType) => state.queryRefactor.coreQuoteDetails,
    (state: FictivStateType) => isCoreSuccessState(state.queryRefactor),
    (state: FictivStateType) => isNoPartState(state.queryRefactor),
    (state: FictivStateType) => isQuoteRequestedState(state.queryRefactor),
    (state: FictivStateType) => isConfiguredState(state.queryRefactor),
    (state: FictivStateType) => isRfqType(state.queryRefactor),
    (state: FictivStateType) => isUnconfiguredPartsState(state.queryRefactor),
    (
        coreQuoteDetails,
        isCoreQuerySuccess,
        isQuoteInNoPartState,
        isQuoteInQuoteRequestedState,
        isQuoteInConfiguredState,
        isQuoteInRfqType,
        isQuoteInUnconfiguredPartsState,
    ) => {
        const quoteData = coreQuoteDetails.coreQuoteDetailsData

        const isImQuoteType = isQuoteTypeIm(quoteData?.type)
        const isReorder = quoteData?.status === REORDERING
        const quoteStatus = quoteData?.status
        const quoteModelsTotalCount = quoteData?.quoteModels?.totalCount
        const quoteModels = quoteData?.quoteModels
        const quoteId = quoteData?.id
        const shouldSkipQlltQuery =
            !quoteData || isImQuoteType || !quoteModelsTotalCount || isReorder

        return {
            quoteId,
            id: quoteData?.id,
            quoteModelsTotalCount,
            quoteModels,
            status: quoteStatus,
            shouldSkipQlltQuery,
            isCoreQuerySuccess,
            isQuoteInNoPartState,
            isQuoteInQuoteRequestedState,
            isQuoteInConfiguredState,
            isQuoteInRfqType,
            isQuoteInUnconfiguredPartsState,
        }
    },
)

export const configurationModalPropsSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers.quote,
    (state: FictivStateType) => state.dfmLayers.selectedQuoteModelRevisions,
    (
        quote,
        selectedRevisions,
    ):
        | { ready: false }
        | {
              ready: true
              quote: typeof quote
              quoteId: string
              quoteStatus: string
              selectedQuoteModelRevisions: typeof selectedRevisions
          } => {
        if (!quote || !selectedRevisions) {
            return { ready: false }
        } else {
            return {
                ready: true,
                quote,
                quoteId: quote.id,
                quoteStatus: quote.status,
                selectedQuoteModelRevisions: selectedRevisions,
            }
        }
    },
)

export const QuoteDetailsSumaryCardSelector = createSelector(
    (state: FictivStateType) => state.queryRefactor.coreQuoteDetails,
    (state: FictivStateType) => isCoreSuccessState(state.queryRefactor),
    (state: FictivStateType) => isNoPartState(state.queryRefactor),
    (state: FictivStateType) => isQuoteRequestedState(state.queryRefactor),
    (state: FictivStateType) => isConfiguredState(state.queryRefactor),
    (state: FictivStateType) => isRfqType(state.queryRefactor),
    (state: FictivStateType) => isUnconfiguredPartsState(state.queryRefactor),
    (
        coreQuoteDetails,
        isCoreQuerySuccess,
        isQuoteInNoPartState,
        isQuoteInQuoteRequestedState,
        isQuoteInConfiguredState,
        isQuoteInRfqType,
        isQuoteInUnconfiguredPartsState,
    ) => {
        const slowestShipDateEstimate =
            coreQuoteDetails.coreQuoteDetailsData?.slowestShipDateEstimate

        const zipCode = coreQuoteDetails.coreQuoteDetailsData?.zipCode
        const type = coreQuoteDetails.coreQuoteDetailsData?.type
        const postalCode = coreQuoteDetails.coreQuoteDetailsData?.zipCode
        const subTotalInCents =
            coreQuoteDetails.coreQuoteDetailsData?.subTotalInCents
        const isTaxExempt = coreQuoteDetails.coreQuoteDetailsData?.isTaxExempt
        const quoteModels = coreQuoteDetails.coreQuoteDetailsData?.quoteModels
        const isOverseasShipping =
            coreQuoteDetails.coreQuoteDetailsData?.isOverseasShipping
        const businessAccountDiscount =
            coreQuoteDetails.coreQuoteDetailsData?.businessAccountDiscount
        const isQuotePurchasable =
            coreQuoteDetails.coreQuoteDetailsData?.isQuotePurchasable
        const partsSummary = coreQuoteDetails.coreQuoteDetailsData?.partsSummary
        const numberOfParts =
            coreQuoteDetails.coreQuoteDetailsData?.numberOfParts
        const isImQuoteType = isQuoteTypeIm(
            coreQuoteDetails.coreQuoteDetailsData?.type,
        )
        const quoteId = coreQuoteDetails.coreQuoteDetailsData?.id
        const orderName =
            coreQuoteDetails.coreQuoteDetailsData?.reorderSourceOrderName ||
            TEST_REORDER_ORDER_NAME
        const status = coreQuoteDetails?.coreQuoteDetailsData?.status
        const isQuoteInRfqState =
            coreQuoteDetails?.coreQuoteDetailsData?.isQuoteInRfqState
        const quoteExpiryDate =
            coreQuoteDetails?.coreQuoteDetailsData?.expiration
        const isExpired = coreQuoteDetails?.coreQuoteDetailsData?.isExpired
        const beganCheckoutAt =
            coreQuoteDetails?.coreQuoteDetailsData?.beganCheckoutAt
        const depositInCents =
            coreQuoteDetails?.coreQuoteDetailsData?.depositInCents
        const isReorder =
            coreQuoteDetails?.coreQuoteDetailsData?.status === REORDERING
        const productionPartsSubtotalInCents =
            coreQuoteDetails?.coreQuoteDetailsData
                ?.productionPartsSubtotalInCents

        return {
            isCoreQuerySuccess,
            quote: {
                productionPartsSubtotalInCents,
                beganCheckoutAt,
                depositInCents,
                zipCode,
                quoteExpiryDate,
                type,
                slowestShipDateEstimate,
                quoteId,
                postalCode,
                subTotalInCents,
                isTaxExempt,
                quoteModels,
                businessAccountDiscount,
                isQuotePurchasable,
                partsSummary,
                numberOfParts,
                isImQuoteType,
                status,
                isExpired,
                isReorder,
                isOverseasShipping,
                orderName,
                isQuoteInRfqState,
                id: quoteId,
            },
            isQuoteInNoPartState,
            isQuoteInQuoteRequestedState,
            isQuoteInConfiguredState,
            isQuoteInRfqType,
            isQuoteInUnconfiguredPartsState,
        }
    },
)

export const quoteDetailsPageSelector = createSelector(
    (state: FictivStateType) => state.queryRefactor.coreQuoteDetails,
    (state: FictivStateType) => isCoreSuccessState(state.queryRefactor),
    (state: FictivStateType) => isCoreLoadingState(state.queryRefactor),
    (state: FictivStateType) => isNoPartState(state.queryRefactor),
    (state: FictivStateType) => isQuoteRequestedState(state.queryRefactor),
    (state: FictivStateType) => isConfiguredState(state.queryRefactor),
    (state: FictivStateType) => isRfqType(state.queryRefactor),
    (state: FictivStateType) => isUnconfiguredPartsState(state.queryRefactor),
    (state: FictivStateType) => isPurchasableStateEnabled(state.identity),

    (
        coreQuoteDetails,
        isCoreQuerySuccess,
        isCoreQueryLoading,
        isQuoteInNoPartState,
        isQuoteInQuoteRequestedState,
        isQuoteInConfiguredState,
        isQuoteInRfqType,
        isQuoteInUnconfiguredPartsState,
        isPurchasableStateEnabledForQuote,
    ) => {
        const quoteData = coreQuoteDetails.coreQuoteDetailsData
        const isReorder = quoteData?.status === REORDERING
        const isRequested = quoteData?.status === REQUESTED
        const quoteModelsItems = quoteData?.quoteModels?.items || []
        const quoteModelsTotalCount = quoteData?.quoteModels?.totalCount
        const isQuoteLocked = isPurchasableStateEnabledForQuote
            ? quoteData?.status !== DRAFT && quoteData?.status != PURCHASABLE
            : quoteData?.status !== DRAFT
        const quoteAttachments = quoteData?.quoteAttachments.items || []
        const coreQuoteError = coreQuoteDetails.errorMessage
        const isLocked = quoteData?.isLocked // This lock status is where a quote gets locked in CPQ/Case Management

        return {
            quoteData,
            isReorder,
            isRequested,
            quoteModelsItems,
            quoteModelsTotalCount,
            isQuoteLocked,
            quoteAttachments,
            coreQuoteError,
            isCoreQuerySuccess,
            isCoreQueryLoading,
            isQuoteInNoPartState,
            isQuoteInQuoteRequestedState,
            isQuoteInConfiguredState,
            isQuoteInRfqType,
            isQuoteInUnconfiguredPartsState,
            isLocked,
        }
    },
)

export const productionSpeedDropdownSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers?.quote,
    isQueryDetailsQueryRefactorOn,
    shouldHideInternationalOrderFlag,

    (
        quote,
        isQueryDetailsQueryRefactorEnabled,
        shouldHideInternationalOrder,
    ) => {
        const quoteId = quote?.id

        return {
            quoteId,
            isQueryDetailsQueryRefactorEnabled,
            shouldHideInternationalOrder,
        }
    },
)
export const quantityFieldSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers?.quote,
    isQueryDetailsQueryRefactorOn,

    (quote, isQueryDetailsQueryRefactorEnabled) => {
        const quoteId = quote?.id

        return {
            quoteId,
            isQueryDetailsQueryRefactorEnabled,
        }
    },
)
